import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { taskApi } from '@/api';
import { QUERY_KEY_CONST, QUERY_META_CONST } from '@/constant';
import {
  TASK_DISTRIBUTE_METHOD,
  type TASK_WORKSTATE,
} from '@/constant/taskConstant';
import { useVToast } from '@/package/kiska/src/component/UI/VToast';
import type {
  Modeling,
  Task,
  TaskAllocation,
  TaskApi,
  TaskSearchParams,
} from '@/type';
import { ERROR_CODE } from '@/util';

import { useTypedSearchParams } from '../searchParams';

export const useTaskCreateMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useVToast();

  return useMutation({
    mutationFn: (payload: TaskApi.Create.Request) => taskApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
    },
    onError: error => {
      if ((error as any).data.resultCode === ERROR_CODE['45423']) {
        addToast({
          variant: 'error',
          message: t('error.ContractExpired.lionization'),
          autoClose: true,
        });
      } else {
        addToast({
          variant: 'error',
          message: t('error.defaultError.coperiodic'),
          autoClose: true,
        });
      }
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });
};

export const useTaskCountQuery = (
  param?: Partial<TaskApi.GetCount.Request>,
) => {
  const { getAll } = useTypedSearchParams<TaskSearchParams>();
  const state = getAll('taskTaskState');

  const listParams: TaskApi.GetCount.Request = {
    ...param,
    taskState: state.length ? (state as TASK_WORKSTATE[]) : null,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value == null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryKey: QUERY_KEY_CONST.TASK_KEYS.getCount(param ?? {}),
    queryFn: () =>
      taskApi.getCount(param ?? {}).then(res => res.data.resultData),
  });
};

export const useInfiniteTaskListQuery = ({
  hasObject,
  hasOperation,
  projectID,
  taskID,
  taskState,
}: Pick<
  TaskApi.GetList.Request,
  'taskState' | 'hasOperation' | 'hasObject' | 'projectID' | 'taskID'
>) => {
  const { get, getAll } = useTypedSearchParams<TaskSearchParams>();

  const limit = get('taskLimit') ? Number(get('taskLimit')) : 2;
  const keyword = get('taskKeyword');
  const state = getAll('taskTaskState');

  const listParams: TaskApi.GetList.Request = {
    hasObject,
    hasOperation,
    keyword,
    limit,
    projectID,
    taskID,
    taskState: taskState ?? (state.length ? (state as TASK_WORKSTATE[]) : null),
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value == null) delete listParams[key as keyof typeof listParams];
  });

  return useInfiniteQuery({
    queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(listParams),
    queryFn: ({ pageParam }) =>
      taskApi.getList({ ...listParams, cursor: pageParam }),
    initialPageParam: '',
    getNextPageParam: nextPage => nextPage.data.cursor,
  });
};

export const useTaskDetailQuery = (id: Task['id']) => {
  return useQuery({
    queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
    queryFn: () =>
      taskApi.getDetail({ pTaskID: id }).then(res => res.data.resultData),
  });
};

export const useTaskUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: TaskApi.Update.Request) =>
      taskApi.update(params).then(res => res.data.resultData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
      });
    },
  });
};

export const useTaskRemoveQuery = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useVToast();

  return useMutation({
    mutationFn: (pTaskID: Task['id']) => {
      return taskApi.remove({ pTaskID });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
    },
    onError: error => {
      if ((error as any).data.resultCode === ERROR_CODE['1403']) {
        addToast({
          variant: 'error',
          message: t('error.modelDependencyError.corectome'),
          autoClose: true,
        });
      }
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });
};

export const useExportTaskLabelQuery = () => {
  return useMutation({
    mutationFn: (taskID: Task['id']) => {
      return taskApi.exportLabel({ pTaskID: taskID });
    },
  });
};

export const useRequestTaskPreAnnotationMutation = () => {
  const { t } = useTranslation();
  const { addToast } = useVToast();

  return useMutation({
    mutationFn: ({
      modelingID,
      taskID,
      useSession,
    }: {
      taskID: Task['id'];
      modelingID: Modeling['id'];
      useSession?: boolean;
    }) => {
      return taskApi.requestTaskPreAnnotation({
        modelingID: `${modelingID}`,
        pTaskID: taskID,
        useSession,
      });
    },
    onError: error => {
      if ((error as any).data.resultCode === ERROR_CODE['41423']) {
        const { limit, used } = JSON.parse((error as any).data.resultMsg) as {
          limit: number;
          used: number;
        };

        addToast({
          variant: 'error',
          message: t(
            'error.AnnotationUsageLimitExceeded.counterpronunciamento',
            {
              limit,
              used,
            },
          ),
          autoClose: true,
        });
      }

      if ((error as any).data.resultCode === ERROR_CODE['44423']) {
        const { limit, used } = JSON.parse((error as any).data.resultMsg) as {
          limit: number;
          used: number;
        };

        addToast({
          variant: 'error',
          message: t('error.TrainingTimeUsageLimitExceeded.creationism', {
            limit,
            used,
          }),
          autoClose: true,
        });
      }
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });
};

export const useRequestDeployment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskID: Task['id']) => {
      return taskApi.requestDeployment({ pTaskID: taskID });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
    },
  });
};

export const useModelTrainingQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskID: Task['id']) => {
      return taskApi.modelTraining({ pTaskID: taskID });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
    },
  });
};

export const useRangeDistibuteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Pick<
        TaskApi.Distribute.Request<TASK_DISTRIBUTE_METHOD.RANGE>,
        'total'
      > & {
        distributions: Pick<
          TaskAllocation<TASK_DISTRIBUTE_METHOD.RANGE>,
          'allocateRange' | 'userID'
        >[];
        pTaskID: Task['id'];
      },
    ) => {
      return taskApi
        .distributeByRange({
          ...params,
          method: TASK_DISTRIBUTE_METHOD.RANGE,
        })
        .then(res => res.data.resultData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getCount(),
      });
    },
  });
};

export const useRatioDistibuteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Pick<
        TaskApi.Distribute.Request<TASK_DISTRIBUTE_METHOD.RATIO>,
        'total'
      > & {
        distributions: TaskAllocation<TASK_DISTRIBUTE_METHOD.RATIO>[];
        pTaskID: Task['id'];
      },
    ) => {
      return taskApi
        .distributeByRatio({
          ...params,
          method: TASK_DISTRIBUTE_METHOD.RATIO,
        })
        .then(res => res.data.resultData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getCount(),
      });
    },
  });
};

export const useDistributePreviewQuery = (
  param: TaskApi.GetDistributePreview.Request,
) => {
  return useQuery({
    queryKey: QUERY_KEY_CONST.TASK_KEYS.getDistributePreview(param),
    queryFn: () =>
      taskApi.getDistributePreview(param).then(res => res.data.resultData),
  });
};

export const useUpdateTaskUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: TaskApi.UpdateTaskUser.Request) =>
      taskApi.updateTaskUser(param).then(res => res.data.resultData),
    onSuccess: (_, param) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail({
          pTaskID: param.taskID,
        }),
      });
    },
  });
};
